import { useEffect, useState } from 'react';
import { Building2, CheckCircle2, ChevronRight, MapPin, Search, Siren } from 'lucide-react';
import { request } from '../api';
import type { SimState } from '../domain/model';
import type { Player } from '../domain/player';
import type { ControlCenterResult, ControlCenterSummary } from '../domain/controlCenters';
import { Brand } from '../App';

type Selection = {
  state: SimState;
  player: Player;
  entry: ControlCenterSummary;
  resources: { areaStations: number; areaVehicles: number };
};

export default function ControlCenterOnboarding({
  player,
  onComplete,
}: {
  player: Player;
  onComplete: (result: Selection) => void;
}) {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<ControlCenterResult | null>(null);
  const [selected, setSelected] = useState<ControlCenterSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      request<ControlCenterResult>(`control-centers?q=${encodeURIComponent(query)}&limit=18`)
        .then((data) => {
          setResult(data);
          if (selected && !data.entries.some((entry) => entry.id === selected.id)) setSelected(null);
        })
        .catch((caught) => setError((caught as Error).message));
    }, query ? 220 : 0);
    return () => window.clearTimeout(timer);
  }, [query, selected]);

  async function confirm() {
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      const response = await request<Selection>('control-centers/select', { id: selected.id });
      onComplete(response);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="onboarding-page">
      <header className="auth-header"><Brand link /><span>Angemeldet als <b>{player.username}</b></span></header>
      <section className="onboarding-dialog" role="dialog" aria-modal="true" aria-labelledby="onboarding-title">
        <div className="onboarding-heading">
          <span className="onboarding-step">ERSTEINRICHTUNG · SCHRITT 1 VON 1</span>
          <div className="onboarding-icon"><Building2 /></div>
          <h1 id="onboarding-title">Lege deine Leitstelle fest</h1>
          <p>Deine Wahl bestimmt das vollständige Einsatzgebiet sowie alle dort hinterlegten Wachen, Hilfsorganisationen und Einsatzmittel.</p>
        </div>
        <label className="onboarding-search">
          <Search />
          <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Leitstelle, Stadt oder Landkreis suchen …" />
          {result && <span>{result.total} Treffer</span>}
        </label>
        <div className="onboarding-list" aria-label="Leitstellen">
          {!result ? <p>Leitstellen werden geladen …</p> : result.entries.length ? result.entries.map((entry) => (
            <button key={entry.id} className={selected?.id === entry.id ? 'selected' : ''} onClick={() => setSelected(entry)}>
              <span className="center-radio">{selected?.id === entry.id && <CheckCircle2 />}</span>
              <span><b>{entry.name}</b><small><MapPin /> {entry.jurisdictions.map((area) => area.name).join(' · ')}</small></span>
              <span className="center-meta">{entry.federalState}<small>{entry.tasks.join(' · ')}</small></span>
              <ChevronRight />
            </button>
          )) : <p>Keine passende Leitstelle gefunden. Suche nach einer Stadt oder einem Landkreis.</p>}
        </div>
        <div className="onboarding-summary">
          <div><Siren /><span><b>Automatischer Dienstbeginn</b><small>Alle verfügbaren Einheiten im Einsatzbereich werden geladen. Danach starten drei automatisch erzeugte Einsätze.</small></span></div>
          <button className="button primary" disabled={!selected || busy} onClick={confirm}>
            {busy ? 'Einsatzbereich wird eingerichtet …' : selected ? `${selected.name} übernehmen` : 'Leitstelle auswählen'}
            {!busy && <ChevronRight />}
          </button>
        </div>
        {error && <p className="auth-error" role="alert">{error}</p>}
      </section>
    </main>
  );
}
